import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

type UserType = 'Shipper' | 'Carrier' | 'Broker';

interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  userType?: string;
  company?: string;
  isAdmin?: boolean;
  emailVerified?: boolean;
}

interface UserContextType {
  userType: UserType;
  setUserType: (type: UserType) => void;
  userName: string;
  user: AuthUser | null;
  isLoading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [userType, setUserType] = useState<UserType>('Shipper');
  const [userName, setUserName] = useState<string>('');
  const [initialized, setInitialized] = useState(false);

  const { data: user, isLoading } = useQuery<AuthUser>({
    queryKey: ['/api/auth/user'],
    retry: false,
  });

  useEffect(() => {
    if (user && !initialized) {
      // Set user type from authenticated user
      if (user.userType) {
        const validTypes: UserType[] = ['Shipper', 'Carrier', 'Broker'];
        if (validTypes.includes(user.userType as UserType)) {
          setUserType(user.userType as UserType);
        }
      }
      // Set user name from authenticated user
      if (user.firstName || user.lastName) {
        setUserName(`${user.firstName || ''} ${user.lastName || ''}`.trim());
      } else if (user.company) {
        setUserName(user.company);
      }
      setInitialized(true);
    }
  }, [user, initialized]);

  return (
    <UserContext.Provider value={{ 
      userType, 
      setUserType,
      userName,
      user: user || null,
      isLoading: isLoading || (!initialized && !user)
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
